import {didTheUserSetUpCommerceForThisSite} from '@packages/systems/commerce/store';
import {getIsSiteUsersActivated} from '@packages/systems/users/flux';
import {weakMemo} from '@packages/utilities/memo';
import {entries} from '@packages/utilities/fp/utils';
// eslint-disable-next-line webflow/no-dangerous-imports
import {getLibrariesWithVariables} from '@packages/systems/libraries-designer';
import type {LoadedLibraries} from '@packages/systems/libraries';
import type {VariableMap} from '@packages/systems/core/utils/StyleVariables';
import type {Feature} from '../types';
import {abilityFeature} from '../abilityFeature';

/**
 * Given a `VariableMap` and a `LoadedLibraries`, checks if there are any installed
 * libraries with variables.
 */
const hasLibrariesWithVariables = weakMemo((libraries: LoadedLibraries) =>
  weakMemo((variables: VariableMap) => {
    const librariesWithVariables = getLibrariesWithVariables(
      libraries,
      variables
    );
    return librariesWithVariables.length > 0;
  })
);

export const features = abilityFeature({
  key: 'features',
  isValid: function isFeaturesValid(
    {
      CommerceStore,
      ContextualOnboardingStore,
      CssVariablesStore,
      DesignerStore,
      SiteDataStore,
      SiteUsersStore,
    },
    featuresToCheck
  ) {
    // any selector can be used here - however, it's worth noting
    // that this will be called in both the designer AND the
    // editor, so optional chaining or other defensive code
    // may be valuable!
    const availability: Record<Feature, boolean> = {
      activityLog:
        SiteDataStore?.isOrg || Boolean(SiteDataStore?.workspaceSlug),
      aiSiteGen: SiteDataStore?.isAISiteGen,
      cloneable: SiteDataStore?.isCloneable && !DesignerStore?.isPreviewLink,
      workspaceCopilotEnabled: Boolean(SiteDataStore?.workspaceCopilotEnabled),
      ecommerce:
        CommerceStore && didTheUserSetUpCommerceForThisSite(CommerceStore),
      onboarding: Boolean(ContextualOnboardingStore?.persistentState),
      siteUsers: SiteUsersStore && getIsSiteUsersActivated(SiteUsersStore),
      librariesWithVariables:
        CssVariablesStore?.variables &&
        DesignerStore?.libraries &&
        hasLibrariesWithVariables(DesignerStore.libraries)(
          CssVariablesStore.variables
        ),
    };

    // for any feature set to either `true` or `false` in `config.features`,
    // we want to deny access when the current feature availability
    // does not match the requirement
    return entries(featuresToCheck).every(
      ([feature, expectation]: [Feature, boolean]) =>
        availability[feature] === expectation
    );
  },
});
